import { ReviewModalItem } from "@/components/display/ReviewModalItem";
import { getBaseThemeName } from "@/features/branding";
import { HacsOmUploadData } from "@/features/hacs";
import { useGetCurrentGroupIdQuery, useGetCurrentUserQuery, useGetGroupsQuery, useGetToolUsageQuery } from "@/store/services/supabase";
import { PortfolioExportAdvancedSettings } from "@/tools/aggregate/portfolio-export/components/PortfolioExportAdvancedSettings";
import { GridItem, PortfolioExportPermissions } from "@/tools/aggregate/portfolio-export/components/PortfolioExportFormItems";
import { usePortfolioTutorialsState } from "@/tools/aggregate/portfolio-export/PortfolioTutorialContext";
import { PortfolioRegionType } from "@/tools/aggregate/portfolio-export/types";
import { useAvailablePortfolioTypes } from "@/utils/hooks/useAvailablePortfolioTypes";
import { useCanCreatePrivatePortfolio } from "@/utils/hooks/useCanCreatePrivate";
import { useIsOwner } from "@/utils/hooks/useIsOwner";
import { useKeys } from "@/utils/hooks/useKeys";
import { removeFileExtension } from "@/utils/string";
import { ArrowUpOutlined, PaperClipOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/nextjs";
import { Alert, Button, Input, Progress, Statistic } from "antd";
import classnames from "classnames";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown, { Components } from "react-markdown";
import s from "./PortfolioExportUploadReview.module.scss";
export interface PortfolioExportUploadReviewProps {
  /** Handles whenever a CSV is selected. */
  setData: (data?: HacsOmUploadData) => void;
  /** File data for the chosen CSV upload. */
  data: HacsOmUploadData;
  onBack: () => void;
  onContinue: () => void;
  isAdvancedSettingsOpen?: boolean;
  portfolioType?: PortfolioRegionType;
}
export const PortfolioExportUploadReview = ({
  data,
  setData,
  onBack,
  onContinue,
  isAdvancedSettingsOpen = false,
  portfolioType
}: PortfolioExportUploadReviewProps) => {
  const {
    data: groups
  } = useGetGroupsQuery();
  const {
    data: currentGroupId
  } = useGetCurrentGroupIdQuery();
  const {
    data: currentUser
  } = useGetCurrentUserQuery();
  const isOwner = useIsOwner();
  const availablePortfolioTypes = useAvailablePortfolioTypes();
  const {
    t: pt
  } = useTranslation("terms", {
    keyPrefix: "portfolioTypes"
  });
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.portfolioInsights.uploadLimitAlert"
  });
  const portfolioTypeTitle = pt(portfolioType + ".title");
  const {
    newRunModalReview
  } = usePortfolioTutorialsState();
  const canCreatePrivate = useCanCreatePrivatePortfolio();
  const [filename, setFilename] = useState(removeFileExtension(data.file.name || ""));
  const [isPrivate, setIsPrivate] = useState(false);
  const [keys] = useKeys();
  const [groupId, setGroupId] = useState("");
  useEffect(() => {
    if (currentGroupId?.id) {
      setGroupId(currentGroupId.id);
    }
  }, [currentGroupId]);
  useEffect(() => {
    if (canCreatePrivate && !keys.orgSecret) {
      setIsPrivate(true);
    } else {
      setIsPrivate(false);
    }
  }, [canCreatePrivate, keys]);
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  if (!toolUsage) return <></>;
  const usage = portfolioType === "global" && getBaseThemeName() === "hkma" ? toolUsage.portfolioInsightsGlobal : toolUsage.singleQuotaMode && toolUsage.singleQuotaUsage ? toolUsage.singleQuotaUsage : toolUsage.portfolioInsights;
  if (!usage || typeof usage.quota !== "number" || usage.quota === 0) {
    Sentry.captureException("portfolioExportUploadReview", {
      tags: {
        section: "Quota Limit Error"
      },
      extra: {
        userId: currentUser?.id,
        orgId: currentUser?.org_id,
        toolUsage,
        currentUsage: usage
      }
    });
  }

  // Don't count the header row.
  const uploadCount = data.lineCount - 1;
  const uploadPct = parseFloat((uploadCount / usage.quota * 100).toFixed(2));
  const futureUsage = usage.usage + uploadCount;
  const futureUsagePct = parseFloat((futureUsage / usage.quota * 100).toFixed(2));
  const usageStat = `${futureUsage.toLocaleString()} / ${usage.quota.toLocaleString()} assets (${futureUsagePct}%)`;
  const usageAfterUploadStat = `${uploadCount.toLocaleString()} (${uploadPct}%)`;
  const exceedsQuotaBuffer = parseFloat(futureUsagePct.toFixed(2)) > usage.hardLimitPercent;
  const showWarning = futureUsagePct > 80 && futureUsagePct <= 100;
  const showError = futureUsagePct > 100;
  const handleContinue = () => {
    setData({
      ...data,
      name: filename,
      groupId: isPrivate && isOwner && keys.orgSecret ? currentGroupId?.id : groupId,
      isPrivate
    });
    onContinue();
  };
  const groupOpts = groups?.map(g => ({
    key: g.id,
    value: g.id,
    label: g.name || ""
  })).filter(val => isOwner ? true : val.key === groupId);
  const components: Components = {
    a: props => {
      return <Link href={props.href || ""} target="_blank">
          {props.children}
        </Link>;
    }
  };
  return <>
      <div className="flex flex-col gap-4">
        <ReviewModalItem ref={newRunModalReview.info} title="Review Upload" data-sentry-element="ReviewModalItem" data-sentry-source-file="PortfolioExportUploadReview.tsx">
          <GridItem data-sentry-element="GridItem" data-sentry-source-file="PortfolioExportUploadReview.tsx">
            <label htmlFor="portfolio-name">Name</label>
            <div className="col-span-2">
              <Input id="portfolio-name" className={s.fileInfoName} value={filename} onChange={val => setFilename(val.target.value)} prefix={<PaperClipOutlined />} data-sentry-element="Input" data-sentry-source-file="PortfolioExportUploadReview.tsx" />
            </div>
          </GridItem>

          <GridItem data-sentry-element="GridItem" data-sentry-source-file="PortfolioExportUploadReview.tsx">
            <div>Assets</div>
            <div className="col-span-2">
              <Statistic value={uploadCount} className={classnames(s.statsValue, "text-sm")} data-sentry-element="Statistic" data-sentry-source-file="PortfolioExportUploadReview.tsx" />
            </div>
          </GridItem>

          {availablePortfolioTypes?.length > 1 && portfolioType && <GridItem>
              <div>Portfolio Type</div>
              <div className="col-span-2">{portfolioTypeTitle}</div>
            </GridItem>}

          <PortfolioExportPermissions isPrivate={isPrivate} setIsPrivate={setIsPrivate} enableShare={!!keys.orgSecret} groupOpts={groupOpts} groupId={groupId} setGroupId={setGroupId} hidePermissionsRadioGroup={!canCreatePrivate} data-sentry-element="PortfolioExportPermissions" data-sentry-source-file="PortfolioExportUploadReview.tsx" />
        </ReviewModalItem>

        <div ref={newRunModalReview.advancedSettings}>
          <PortfolioExportAdvancedSettings defaultOpen={isAdvancedSettingsOpen} data={data} setData={setData} data-sentry-element="PortfolioExportAdvancedSettings" data-sentry-source-file="PortfolioExportUploadReview.tsx" />
        </div>

        <ReviewModalItem ref={newRunModalReview.usage} title="Usage Confirmation" data-sentry-element="ReviewModalItem" data-sentry-source-file="PortfolioExportUploadReview.tsx">
          <GridItem className="!items-start" data-sentry-element="GridItem" data-sentry-source-file="PortfolioExportUploadReview.tsx">
            <div>
              {availablePortfolioTypes.length > 1 && portfolioType ? <>
                  {portfolioTypeTitle} Portfolio
                  <br />
                  usage after upload
                </> : "Usage after upload"}
            </div>

            <div className="col-span-2 flex w-full flex-row justify-between gap-1 pr-2">
              <div className="col-span-2 flex flex-col gap-1">
                <Statistic value={usageStat} className="*:!text-sm" data-sentry-element="Statistic" data-sentry-source-file="PortfolioExportUploadReview.tsx" />
                <Statistic value={usageAfterUploadStat} className="*:!text-sm *:!text-gray-400" suffix={<ArrowUpOutlined />} data-sentry-element="Statistic" data-sentry-source-file="PortfolioExportUploadReview.tsx" />
              </div>
              <Progress type="circle" percent={futureUsagePct} strokeWidth={16} size={42} success={{
              percent: usage.usagePercent,
              strokeColor: "#cdcdcd"
            }} strokeColor={"var(--color-accent)"} showInfo={false} style={{
              gridColumn: "2 / 4"
            }} className="max-w-8" data-sentry-element="Progress" data-sentry-source-file="PortfolioExportUploadReview.tsx" />
            </div>
          </GridItem>
        </ReviewModalItem>

        {showWarning && <Alert message={<Markdown components={components}>{isOwner ? t("owner.warning") : t("user.warning")}</Markdown>} type="warning" showIcon className={s.alert} />}

        {showError && <Alert message={<div>
                <div>
                  <strong>Limit Reached</strong>
                </div>
                <Markdown components={components}>{isOwner ? t("owner.error") : t("user.error")}</Markdown>
              </div>} type="error" showIcon className={s.alert} />}

        <div ref={newRunModalReview.runAnalysisTrigger} className={s.actionButtons}>
          <Button type="text" onClick={onBack} data-sentry-element="Button" data-sentry-source-file="PortfolioExportUploadReview.tsx">
            Back
          </Button>
          <Button type="primary" onClick={handleContinue} disabled={usage.alwaysEnabled ? false : usage.enabled ? exceedsQuotaBuffer : true} data-sentry-element="Button" data-sentry-source-file="PortfolioExportUploadReview.tsx">
            Run Analysis
          </Button>
        </div>
      </div>
    </>;
};